import { useEffect, useMemo, useRef, useState } from "react";
import {
  DownloadOutlined,
  LeftOutlined,
  LinkOutlined,
  RightOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, InputNumber, Space, Spin, Typography } from "antd";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";
import type { TextItem } from "pdfjs-dist/types/src/display/api";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useT } from "@trust/i18n";
import {
  ControlledPdfError,
  loadControlledPdf,
  discoverControlledPdf,
  type PdfFailure,
  type PdfRequest,
} from "./controlledPdf";

// pdf.js worker 走 Vite 资产打包(?url import),离线可构建,不依赖运行时 CDN。
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
// Keep CJK character maps and fallback fonts on the same origin and PDF.js version.
const PDF_RESOURCE_OPTIONS = {
  cMapUrl: `/assets/pdfjs/${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `/assets/pdfjs/${pdfjs.version}/standard_fonts/`,
};

interface Props {
  pdfUrl: string | null;
  originLink: string | null;
  /** AI 评审证据原文片段:在 PDF 全文中检索并跳转到命中页,textLayer 黄色高亮。 */
  highlightText?: string;
  sourceRequest?: PdfRequest;
}

/** 一个命中:page 为 1-based 页码;start/end 是归一化页文本里的字符区间。 */
interface MatchRange {
  page: number;
  start: number;
  end: number;
  variant: "spaced" | "compact";
}

/** 归一化索引:starts[i]/lens[i] 是第 i 个文本项在归一化页文本中的起点与长度。 */
interface VariantIndex {
  text: string;
  starts: number[];
  lens: number[];
}

/** 单页文本索引。items 与 textLayer 渲染共用,保证命中区间能对齐到 span。 */
interface PageTextIndex {
  items: TextItem[];
  /** 项间补空格的归一化(主匹配,兼容换行/空白差异) */
  spaced: VariantIndex;
  /** 去掉全部空白后的归一化(兜底,兼容被拆词的命中) */
  compact: VariantIndex;
}

const SCALE_MIN = 0.5;
const SCALE_MAX = 3;
const SCALE_STEP = 0.25;

/** 空白/换行归一化:NFKC + 连续空白折叠为单空格 + 小写,保证证据引用能命中原文。 */
function normalizeText(s: string): string {
  return s.normalize("NFKC").replace(/\s+/g, " ").toLowerCase();
}

function buildVariant(items: TextItem[], sep: " " | ""): VariantIndex {
  const starts: number[] = [];
  const lens: number[] = [];
  let text = "";
  for (const item of items) {
    const norm = normalizeText(item.str);
    if (
      sep === " " &&
      text.length > 0 &&
      norm.length > 0 &&
      !text.endsWith(" ") &&
      !norm.startsWith(" ")
    ) {
      text += " ";
    }
    starts.push(text.length);
    text += norm;
    lens.push(norm.length);
  }
  return { text, starts, lens };
}

/** 抽取一页的文本并建立索引,按页缓存。 */
async function getPageTextIndex(
  page: PDFPageProxy,
  cache: Map<number, PageTextIndex>,
): Promise<PageTextIndex> {
  const hit = cache.get(page.pageNumber);
  if (hit) return hit;
  const content = await page.getTextContent();
  const items = content.items.filter((it): it is TextItem => "str" in it);
  const index: PageTextIndex = {
    items,
    spaced: buildVariant(items, " "),
    compact: buildVariant(items, ""),
  };
  cache.set(page.pageNumber, index);
  return index;
}

function collectMatches(
  v: VariantIndex,
  page: number,
  variant: "spaced" | "compact",
  needle: string,
  out: MatchRange[],
) {
  let pos = v.text.indexOf(needle);
  while (pos !== -1) {
    out.push({ page, start: pos, end: pos + needle.length, variant });
    pos = v.text.indexOf(needle, pos + needle.length);
  }
}

/** 自绘 textLayer:透明 span 覆盖在 canvas 上,供选中/复制与高亮背景使用。 */
function buildTextLayer(
  container: HTMLDivElement,
  items: TextItem[],
  viewport: pdfjs.PageViewport,
): HTMLSpanElement[] {
  container.textContent = "";
  const spans: HTMLSpanElement[] = [];
  for (const item of items) {
    const span = document.createElement("span");
    span.textContent = item.str;
    const tx = pdfjs.Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(tx[2], tx[3]);
    span.style.left = `${tx[4]}px`;
    span.style.top = `${tx[5] - fontHeight}px`;
    span.style.fontSize = `${fontHeight}px`;
    container.appendChild(span);
    spans.push(span);
  }
  // 先统一读宽度(一次回流),再统一写 transform,避免逐 span 布局抖动
  const ratios: number[] = [];
  for (let i = 0; i < spans.length; i++) {
    const target = items[i].width * viewport.scale;
    const actual = spans[i].getBoundingClientRect().width;
    ratios.push(target > 0 && actual > 0 ? target / actual : 1);
  }
  for (let i = 0; i < spans.length; i++) {
    if (Math.abs(ratios[i] - 1) > 0.01) spans[i].style.transform = `scaleX(${ratios[i]})`;
  }
  return spans;
}

const TEXT_LAYER_CSS = `
.pdf-stage { background: #525659; border: 1px solid #f0f0f0; border-radius: 4px; overflow: auto; max-height: 720px; }
.pdf-page-box { position: relative; margin: 12px auto; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,.3); width: fit-content; }
.pdf-text-layer { position: absolute; inset: 0; overflow: hidden; line-height: 1; }
.pdf-text-layer span { position: absolute; color: transparent; white-space: pre; cursor: text; transform-origin: 0 0; }
.pdf-text-layer ::selection { background: rgba(0, 96, 255, 0.3); }
.pdf-text-layer span.pdf-hl { background: rgba(255, 213, 0, 0.4); border-radius: 2px; }
.pdf-text-layer span.pdf-hl-active { background: rgba(255, 152, 0, 0.55); border-radius: 2px; }
`;

export function PdfViewer({ pdfUrl, originLink, highlightText, sourceRequest }: Props) {
  const t = useT();

  const controlled = sourceRequest && sourceRequest.kind !== "legacy";
  const [failure, setFailure] = useState<PdfFailure | "invalidRequest" | "pageOutOfRange" | null>(
    null,
  );
  const [discovering, setDiscovering] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [pageRendered, setPageRendered] = useState(false);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [rendering, setRendering] = useState(false);
  const [renderSeq, setRenderSeq] = useState(0);
  const [matches, setMatches] = useState<MatchRange[]>([]);
  const [active, setActive] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const spansRef = useRef<HTMLSpanElement[]>([]);
  const textCacheRef = useRef<Map<number, PageTextIndex>>(new Map());
  const searchGenRef = useRef(0);
  const pendingScrollRef = useRef(false);

  // Controlled sources never use the legacy production URL, including error/invalid states.
  useEffect(() => {
    const abort = new AbortController();
    let cancelled = false;
    let task: ReturnType<typeof pdfjs.getDocument> | undefined;
    let blobUrl: string | undefined;
    setDoc(null);
    setNumPages(0);
    setDownloadUrl(null);
    setDiscovering(false);
    setFailure(null);
    setPageRendered(false);
    setPage(1);
    setScale(1);
    setMatches([]);
    setSearched(false);
    textCacheRef.current.clear();
    if (sourceRequest?.kind === "invalid") {
      setFailure("invalidRequest");
      setStatus("error");
      return;
    }
    if (!pdfUrl && sourceRequest?.kind !== "controlled" && sourceRequest?.kind !== "discover") {
      setStatus("idle");
      return;
    }
    setStatus("loading");
    void (async () => {
      try {
        let resolvedRequest = sourceRequest;
        if (sourceRequest?.kind === "discover") {
          setDiscovering(true);
          resolvedRequest = await discoverControlledPdf(sourceRequest.guideId, abort.signal);
          if (cancelled) return;
          setDiscovering(false);
        }
        if (resolvedRequest?.kind === "controlled") {
          const data = await loadControlledPdf(resolvedRequest, abort.signal);
          if (cancelled) return;
          // Preserve independently verified bytes for explicit downloads; pdf.js may transfer data.
          blobUrl = URL.createObjectURL(new Blob([data.slice()], { type: "application/pdf" }));
          task = pdfjs.getDocument({ ...PDF_RESOURCE_OPTIONS, data });
        } else {
          if (!pdfUrl) return;
          task = pdfjs.getDocument({ ...PDF_RESOURCE_OPTIONS, url: pdfUrl });
        }
        const d = await task.promise;
        if (cancelled) return;
        const requestedPage = resolvedRequest?.kind === "controlled" ? resolvedRequest.page : 1;
        if (requestedPage > d.numPages) {
          setFailure("pageOutOfRange");
          setStatus("error");
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          void task.destroy();
          return;
        }
        setDoc(d);
        setNumPages(d.numPages);
        setPage(requestedPage);
        setDownloadUrl(blobUrl ?? pdfUrl);
        setStatus("ready");
      } catch (error) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        if (!cancelled) {
          setFailure(error instanceof ControlledPdfError ? error.reason : "loadFailed");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
      void task?.destroy();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [pdfUrl, sourceRequest]);

  // 渲染当前页(canvas + textLayer);只渲染可视页,翻页/缩放才重绘
  useEffect(() => {
    if (!doc || status !== "ready") return;
    const canvas = canvasRef.current;
    const layer = layerRef.current;
    if (!canvas || !layer) return;
    let cancelled = false;
    let renderTask: RenderTask | null = null;
    setRendering(true);
    setPageRendered(false);
    (async () => {
      try {
        const p = await doc.getPage(page);
        if (cancelled) return;
        const viewport = p.getViewport({ scale });
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        renderTask = p.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        await renderTask.promise;
        if (cancelled) return;
        const index = await getPageTextIndex(p, textCacheRef.current);
        if (cancelled) return;
        spansRef.current = buildTextLayer(layer, index.items, viewport);
        setRenderSeq((s) => s + 1);
        setPageRendered(true);
      } catch (err) {
        if (!cancelled && (err as Error)?.name !== "RenderingCancelledException")
          setStatus("error");
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [doc, status, page, scale]);

  // 全文检索证据片段:先 spaced 归一化匹配,零命中再用 compact(去空白)兜底;逐页增量出结果
  useEffect(() => {
    if (!doc || status !== "ready") return;
    const needle = highlightText ? normalizeText(highlightText).trim() : "";
    setMatches([]);
    setActive(0);
    setSearched(false);
    if (!needle) {
      setSearching(false);
      return;
    }
    const gen = ++searchGenRef.current;
    setSearching(true);
    const needleCompact = needle.replace(/ /g, "");
    let jumped = false;
    const found: MatchRange[] = [];
    const scanPage = async (pno: number, variant: "spaced" | "compact", n: string) => {
      const p = await doc.getPage(pno);
      const index = await getPageTextIndex(p, textCacheRef.current);
      collectMatches(variant === "spaced" ? index.spaced : index.compact, pno, variant, n, found);
    };
    const onFirstHit = (pno: number) => {
      if (jumped || found.length === 0) return;
      jumped = true;
      setActive(0);
      setPage(pno);
      pendingScrollRef.current = true;
    };
    (async () => {
      for (let pno = 1; pno <= doc.numPages; pno++) {
        if (gen !== searchGenRef.current) return;
        await scanPage(pno, "spaced", needle);
        onFirstHit(pno);
        setMatches([...found]);
      }
      if (found.length === 0 && needleCompact.length > 0 && needleCompact !== needle) {
        for (let pno = 1; pno <= doc.numPages; pno++) {
          if (gen !== searchGenRef.current) return;
          await scanPage(pno, "compact", needleCompact);
          onFirstHit(pno);
          setMatches([...found]);
        }
      }
      if (gen !== searchGenRef.current) return;
      setSearching(false);
      setSearched(true);
    })();
  }, [doc, status, highlightText]);

  const pageMatches = useMemo(() => matches.filter((m) => m.page === page), [matches, page]);
  const activeMatch = active < matches.length ? matches[active] : undefined;

  // 渲染完成后按当前页命中区间给 span 加高亮类;首个当前命中滚入视野
  // biome-ignore lint/correctness/useExhaustiveDependencies: renderSeq 是有意的触发器——每页渲染完成后重挂高亮
  useEffect(() => {
    const spans = spansRef.current;
    if (spans.length === 0) return;
    for (const span of spans) span.classList.remove("pdf-hl", "pdf-hl-active");
    let firstActiveSpan: HTMLSpanElement | null = null;
    for (const m of pageMatches) {
      const isActive = activeMatch === m;
      // 需要该页的索引做区间→span 映射;索引在渲染/检索时必然已入缓存
      const index = textCacheRef.current.get(m.page);
      if (!index) continue;
      const v = m.variant === "spaced" ? index.spaced : index.compact;
      for (let i = 0; i < index.items.length; i++) {
        if (v.lens[i] === 0) continue;
        const s = v.starts[i];
        const e = s + v.lens[i];
        if (m.start < e && m.end > s) {
          spans[i].classList.add(isActive ? "pdf-hl-active" : "pdf-hl");
          if (isActive && !firstActiveSpan) firstActiveSpan = spans[i];
        }
      }
    }
    if (pendingScrollRef.current && firstActiveSpan) {
      firstActiveSpan.scrollIntoView({ block: "center", behavior: "smooth" });
      pendingScrollRef.current = false;
    }
  }, [renderSeq, pageMatches, activeMatch]);

  const gotoMatch = (next: number) => {
    if (matches.length === 0) return;
    const idx = (next + matches.length) % matches.length;
    setActive(idx);
    setPage(matches[idx].page);
    pendingScrollRef.current = true;
  };

  if (!pdfUrl && !originLink && !controlled) return null;
  const offeredDownload = controlled ? downloadUrl : pdfUrl;
  const visibleFailure = discovering && failure === "loadFailed" ? "discoveryFailed" : failure;

  return (
    <Card
      title={t("pdfViewer.title")}
      size="small"
      extra={
        <Space>
          {offeredDownload && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={offeredDownload}
              target="_blank"
              rel="noreferrer"
            >
              {t("pdfViewer.download")}
            </Button>
          )}
          {originLink && (
            <Button icon={<LinkOutlined />} href={originLink} target="_blank" rel="noreferrer">
              {t(controlled ? "pdfViewer.externalOrigin" : "pdfViewer.visitOrigin")}
            </Button>
          )}
        </Space>
      }
    >
      <style>{TEXT_LAYER_CSS}</style>
      {controlled && status === "ready" && (
        <Alert
          type="success"
          showIcon
          message={t(pageRendered ? "pdfViewer.verifiedRendered" : "pdfViewer.verifiedRendering")}
          style={{ marginBottom: 8 }}
        />
      )}
      {!pdfUrl && !controlled ? (
        <Alert type="info" showIcon message={t("pdfViewer.noPdf")} />
      ) : status === "error" ? (
        <Alert
          type="error"
          showIcon
          message={t(
            controlled ? `pdfViewer.${visibleFailure ?? "loadFailed"}` : "pdfViewer.loadError",
          )}
        />
      ) : (
        <>
          <Space wrap style={{ marginBottom: 8 }}>
            <Button
              size="small"
              icon={<LeftOutlined />}
              disabled={page <= 1 || status !== "ready"}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("pdfViewer.prevPage")}
            </Button>
            <InputNumber
              size="small"
              min={1}
              max={numPages}
              value={page}
              disabled={status !== "ready"}
              onChange={(v) => {
                const n = typeof v === "number" ? v : Number(v);
                if (Number.isFinite(n)) setPage(Math.min(numPages, Math.max(1, Math.trunc(n))));
              }}
              style={{ width: 64 }}
            />
            <Typography.Text type="secondary">
              / {numPages} {t("pdfViewer.pageUnit")}
            </Typography.Text>
            <Button
              size="small"
              icon={<RightOutlined />}
              disabled={page >= numPages || status !== "ready"}
              onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            >
              {t("pdfViewer.nextPage")}
            </Button>
            <Button
              size="small"
              icon={<ZoomOutOutlined />}
              disabled={scale <= SCALE_MIN || status !== "ready"}
              onClick={() => setScale((s) => Math.max(SCALE_MIN, +(s - SCALE_STEP).toFixed(2)))}
              aria-label={t("pdfViewer.zoomOut")}
            />
            <Typography.Text type="secondary">{Math.round(scale * 100)}%</Typography.Text>
            <Button
              size="small"
              icon={<ZoomInOutlined />}
              disabled={scale >= SCALE_MAX || status !== "ready"}
              onClick={() => setScale((s) => Math.min(SCALE_MAX, +(s + SCALE_STEP).toFixed(2)))}
              aria-label={t("pdfViewer.zoomIn")}
            />
            {highlightText?.trim() && (
              <Space size={4} style={{ marginLeft: 16 }}>
                <Typography.Text type="secondary">{t("pdfViewer.evidence")}:</Typography.Text>
                {searching ? (
                  <Typography.Text type="secondary">{t("pdfViewer.searching")}</Typography.Text>
                ) : searched && matches.length === 0 ? (
                  <Typography.Text type="warning">{t("pdfViewer.notFound")}</Typography.Text>
                ) : (
                  <>
                    <Typography.Text>
                      {matches.length > 0 ? `${active + 1} / ${matches.length} ` : ""}
                      {t("pdfViewer.matchUnit")}
                    </Typography.Text>
                    <Button
                      size="small"
                      icon={<VerticalLeftOutlined />}
                      disabled={matches.length === 0}
                      onClick={() => gotoMatch(active - 1)}
                      aria-label={t("pdfViewer.prevMatch")}
                    />
                    <Button
                      size="small"
                      icon={<VerticalRightOutlined />}
                      disabled={matches.length === 0}
                      onClick={() => gotoMatch(active + 1)}
                      aria-label={t("pdfViewer.nextMatch")}
                    />
                  </>
                )}
              </Space>
            )}
          </Space>
          <div
            className="pdf-stage"
            ref={stageRef}
            data-pdf-state={status}
            data-pdf-page={page}
            data-pdf-rendered={pageRendered ? "true" : "false"}
          >
            {status === "loading" && (
              <div style={{ padding: 48, textAlign: "center" }}>
                <Spin />
                <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                  {t(discovering ? "pdfViewer.discovering" : "pdfViewer.loading")}
                </Typography.Text>
              </div>
            )}
            {status === "ready" && (
              <Spin spinning={rendering} size="small">
                <div className="pdf-page-box">
                  <canvas ref={canvasRef} style={{ display: "block" }} />
                  <div ref={layerRef} className="pdf-text-layer" />
                </div>
              </Spin>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
